import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Avisa al cerrar la pestaña y al seguir un enlace interno si hay cambios locales.
 * Los enlaces externos y los que abren otra pestaña no se interceptan.
 */
export function useWarnUnsavedLeave(dirty: boolean) {
    const navigate = useNavigate();
    const [pendingHref, setPendingHref] = useState<string | null>(null);

    useEffect(() => {
        if (!dirty) return;
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };
        const onClick = (event: MouseEvent) => {
            if (event.defaultPrevented || event.button !== 0) return;
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            const target = event.target;
            if (!(target instanceof Element)) return;
            const anchor = target.closest('a');
            if (!anchor) return;
            if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
            const href = anchor.getAttribute('href');
            if (!href || href.startsWith('#') || /^[a-z]+:/i.test(href)) return;
            event.preventDefault();
            event.stopPropagation();
            setPendingHref(href);
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('click', onClick, true);
        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            document.removeEventListener('click', onClick, true);
        };
    }, [dirty]);

    return {
        pendingHref,
        cancelLeave: () => setPendingHref(null),
        confirmLeave: () => {
            const href = pendingHref;
            setPendingHref(null);
            if (href) navigate(href);
        },
    };
}
